import React, { useMemo, useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';

import DataTable from '../ui/DataTable';
import Badge from '../ui/Badge';
import Button from '../ui/Button';

const FILTER_PILLS = [
  { value: 'all', label: 'All' },
  { value: 'real', label: 'Real Incidents' },
  { value: 'drills', label: 'Drills' },
];

const STATUS_PILLS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Active', label: 'Active' },
  { value: 'Closed', label: 'Closed' },
];

const STATUS_BADGE = { Pending: 'amber', Active: 'green', Closed: 'neutral', Rejected: 'red' };

function AnnouncementList({ title }) {

  const [announcements, drills] = useOutletContext();

  const navigate = useNavigate();

  const [filter, setFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const headCells = [
    {
      id: 'id',
      label: 'ID',
      render: (row) => <span className="font-mono text-xs text-neutral-500">{row.id}</span>,
    },
    {
      id: 'title',
      label: 'Title & Description',
      render: (row) => (
        <div>
          <div className="font-bold text-neutral-800">{row.title}</div>
          <div className="text-xs text-neutral-500 mt-0.5 line-clamp-1">{row.description}</div>
        </div>
      ),
    },
    {
      id: 'incident_type.incident_type_name',
      label: 'Type',
      render: (row) => (
        <div className="flex flex-wrap items-center gap-1.5">
          {row.incident_type?.incident_type_name && <Badge>{row.incident_type.incident_type_name}</Badge>}
          <Badge variant={row.drill ? 'blue' : 'neutral'}>{row.drill ? 'Drill' : 'Real Incident'}</Badge>
        </div>
      ),
    },
    {
      id: 'location',
      label: 'Location',
      render: (row) => <span className="text-neutral-600">{row.location || '—'}</span>,
    },
    {
      id: 'isMajor',
      label: 'Severity',
      render: (row) => <Badge variant={row.isMajor ? 'red' : 'green'}>{row.isMajor ? 'Major' : 'Standard'}</Badge>,
    },
    {
      id: 'status',
      label: 'Status',
      render: (row) => <Badge variant={STATUS_BADGE[row.status] || 'neutral'}>{row.status || 'Pending'}</Badge>,
    },
  ];

  const baseRows = useMemo(() => {
    if (filter === 'drills') return drills || [];
    if (filter === 'real') return announcements || [];
    return [...(announcements || []), ...(drills || [])];
  }, [filter, announcements, drills]);

  const rows = useMemo(() => {
    let result = baseRows;
    if (statusFilter !== 'all') {
      result = result.filter((row) => (row.status || 'Pending') === statusFilter);
    }
    const q = search.trim().toLowerCase();
    if (!q) return result;
    return result.filter((row) =>
      [row.title, row.description, row.location]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q))
    );
  }, [baseRows, statusFilter, search]);

  const createLabel = title === 'Announcements' ? 'New Announcement' : 'New Drill';

  return (
    <div className="space-y-6">
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex p-1 bg-white border border-neutral-200 rounded-lg shadow-sm">
            {FILTER_PILLS.map((pill) => (
              <button
                key={pill.value}
                onClick={() => setFilter(pill.value)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  filter === pill.value
                    ? 'bg-seha-50 text-seha-700 shadow-sm'
                    : 'text-neutral-600 hover:bg-neutral-50'
                }`}
              >
                {pill.label}
              </button>
            ))}
          </div>
          <div className="flex p-1 bg-white border border-neutral-200 rounded-lg shadow-sm">
            {STATUS_PILLS.map((pill) => (
              <button
                key={pill.value}
                onClick={() => setStatusFilter(pill.value)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  statusFilter === pill.value
                    ? 'bg-seha-50 text-seha-700 shadow-sm'
                    : 'text-neutral-600 hover:bg-neutral-50'
                }`}
              >
                {pill.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              type="search"
              className="form-input pl-9 w-64"
              placeholder="Search announcements..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <Button
          icon={Plus}
          onClick={() => title === "Announcements" ? navigate("/activation/announce") : navigate("/activation/simulate")}
        >
          {createLabel}
        </Button>
      </div>

      <DataTable
        title={title}
        rows={rows}
        headCells={headCells}
        onView={(row) => navigate(row.status === 'Closed' ? `/closed-incident/${row.id}` : `/active-incident/${row.id}`)}
        emptyLabel="No announcements found."
      />
    </div>
  )
}

export default AnnouncementList
